import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@staff-app/ui';
import type { IntlAllRequest, IntlAllShipment } from '@staff-app/shared-types';
import { useClearIntlAllShopifyClaim } from '../hooks/useIntlAllRequests';

/**
 * CLEAR STUCK ORDER — root only (Paul, 2026-10-27).
 *
 * "Create Shopify order" first marks the shipment as being created, so the two api pods cannot
 * both create it, then asks Shopify, then records the order. A pod dying between those steps leaves
 * the mark with no order, and nobody can tell from here whether Shopify made one — so Create stays
 * refused. This is the way out: look in Shopify, then either LINK the order that exists, or CLEAR
 * the mark so Create can be pressed again. Clearing when an order exists ships the goods twice,
 * which is why the dialog makes the operator say which it is.
 */
export function ClearStuckOrderDialog({
  request,
  shipment,
  open,
  onOpenChange,
}: {
  request: IntlAllRequest;
  shipment: IntlAllShipment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [orderName, setOrderName] = useState('');
  const [clear, { loading, error }] = useClearIntlAllShopifyClaim();

  const run = async (link: boolean) => {
    if (loading || (link && !orderName.trim())) return;
    try {
      await clear({
        variables: {
          input: {
            requestId: request.id,
            route: shipment.route,
            shopifyOrderName: link ? orderName.trim() : undefined,
          },
        },
      });
      onOpenChange(false);
    } catch {
      // Surfaced from `error` below.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="air-clear-stuck-dialog">
        <DialogHeader>
          <DialogTitle>{`Clear stuck order — ${shipment.routeLabel}, ${request.requestNo}`}</DialogTitle>
          <DialogDescription>
            {`A Shopify order was started${shipment.shopifyOrderedBy ? ` by ${shipment.shopifyOrderedBy}` : ''} `}
            {'but never recorded here, so we cannot tell whether it exists.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p>
            {'In the Shopify admin, search orders for the tag '}
            <code className="rounded bg-muted px-1 font-mono" data-testid="air-clear-stuck-tag">
              {`air-${request.id}`}
            </code>
            {'.'}
          </p>
          <div>
            <Label className="text-xs font-medium text-muted-foreground">{'Found it? Its order number'}</Label>
            <div className="mt-1 flex gap-2">
              <Input
                value={orderName}
                onChange={(e) => setOrderName(e.target.value)}
                placeholder="#28860"
                disabled={loading}
                data-testid="air-clear-stuck-order"
              />
              <Button
                onClick={() => run(true)}
                disabled={loading || !orderName.trim()}
                data-testid="air-clear-stuck-link"
              >
                {'Link order'}
              </Button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {'Matched exactly against Shopify before it is saved.'}
            </p>
          </div>
          <div className="rounded-md border border-destructive/40 p-3">
            <p className="mb-2">
              {'No order with that tag? Clear the mark and press "Create Shopify order" again. '}
              <strong>{'Only if you checked'}</strong>
              {' — clearing when an order exists ships the goods twice.'}
            </p>
            <Button
              variant="destructive"
              onClick={() => run(false)}
              disabled={loading}
              data-testid="air-clear-stuck-clear"
            >
              {'No order in Shopify — clear'}
            </Button>
          </div>
          {error ? (
            <p className="text-destructive" data-testid="air-clear-stuck-error">
              {error.message}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            {'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
